import { Sources } from "../models/Chat";

export interface StreamUpdate {
    accumulatedContent: string;
    id?: string;
    sources?: Sources;
    seeds?: string[];
    retrievedChunkIds?: string[];
    usedChunkIds?: string[];
    annotatedAnswer?: string;
    safetyRoute?: any;
    responseMode?: string;
    workflowMetadata?: any;
    safetyClassification?: any;
    retrievalDecision?: any;
    conversationMomentum?: any;
    alignment?: any;
    retrievalSummary?: any;
    toolTrace?: unknown;
    goal?: unknown;
    agenticProgress?: {
        step: string;
        status: string;
        message: string;
    };
    useSynapse?: boolean;
    isComplete?: boolean;
    receivedDone?: boolean;
    persistedChatId?: string;
}

/** Raised for an SSE `event: error` frame so callers surface their own message
 *  instead of the server's. */
export class ChatStreamError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ChatStreamError';
    }
}

/** SSE frames carry an optional `event:` name alongside `data:`. Without reading
 *  it an `event: error` frame is indistinguishable from a content chunk, and its
 *  payload — an internal error message — gets appended to the assistant's reply. */
function extractSseEvent(eventBlock: string): { eventType: string | null; payload: string | null } {
    // SSE payloads can include multiple `data:` lines for a single logical event.
    // We join them here before parsing so callers see one coherent update.
    const lines = eventBlock
        .split('\n')
        .map((line) => line.trim());
    const eventType = lines
        .find((line) => line.startsWith('event:'))
        ?.slice(6)
        .trim() || null;
    const payloadLines = lines
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart());

    if (payloadLines.length === 0) {
        return { eventType, payload: null };
    }

    return { eventType, payload: payloadLines.join('\n').trim() };
}

export async function consumeChatStream(
    response: Response,
    onUpdate: (update: StreamUpdate) => void,
    signal?: AbortSignal
) : Promise<StreamUpdate> {
    if (!response.body) {
        return { accumulatedContent: '', isComplete: false };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedContent = '';
    let buffer = '';
    let latestUpdate: StreamUpdate = { accumulatedContent: '' };
    let sawCompletion = false;
    let stopReading = false;

    try {
        while (!stopReading) {
            if (signal?.aborted) {
                await reader.cancel();
                break;
            }

            const { done, value } = await reader.read();
            if (done) {
                buffer += decoder.decode();
            } else {
                buffer += decoder.decode(value, { stream: true });
            }

            const events = buffer.split('\n\n');
            buffer = events.pop() || '';

            for (const event of events) {
                const { eventType, payload } = extractSseEvent(event);
                if (eventType === 'error') {
                    throw new ChatStreamError(payload || 'The chat stream failed.');
                }
                if (!payload || payload === '[DONE]') {
                    if (payload === '[DONE]') {
                        sawCompletion = true;
                        stopReading = true;
                        break;
                    }
                    continue;
                }

                // If payload is not JSON-like, treat it as a raw text chunk
                if (!payload.startsWith('{')) {
                    accumulatedContent += payload;
                    latestUpdate = { ...latestUpdate, accumulatedContent };
                    onUpdate(latestUpdate);
                    continue;
                }

                try {
                    const data = JSON.parse(payload);
                    const update: StreamUpdate = {
                        accumulatedContent,
                        // Persist ID from previous chunks if missing in this one
                        id: data.id || data.exchangeId || latestUpdate.id
                    };

                    if (data.object === "chat.completion.progress" && data.progress) {
                        latestUpdate = {
                            ...latestUpdate,
                            accumulatedContent,
                            id: data.id || latestUpdate.id,
                            agenticProgress: {
                                step: data.progress.step,
                                status: data.progress.status,
                                message: data.progress.message,
                            },
                        };

                        onUpdate(latestUpdate);
                        continue;
                    }

                    if (data.object === "chat.persistence.ack" && typeof data.chatId === "string") {
                        latestUpdate = {
                            ...latestUpdate,
                            accumulatedContent,
                            persistedChatId: data.chatId,
                        };
                        onUpdate(latestUpdate);
                        continue;
                    }

                    if (data.choices?.[0]?.delta?.content) {
                        accumulatedContent += data.choices[0].delta.content;
                        update.accumulatedContent = accumulatedContent;
                    }

                    if (typeof data.responseMode === 'string') {
                        update.responseMode = data.responseMode;
                    }

                    if (typeof data.useSynapse === 'boolean') {
                        update.useSynapse = data.useSynapse;
                    }

                    if (data.choices?.[0]?.finish_reason === 'stop') {
                        if (data.annotatedAnswer) update.annotatedAnswer = data.annotatedAnswer;
                        if (data.sources) update.sources = data.sources;
                        if (data.safetyRoute) update.safetyRoute = data.safetyRoute;
                        if (data.workflowMetadata) update.workflowMetadata = data.workflowMetadata;

                        update.safetyClassification =
                            data.safetyClassification ??
                            data.workflowMetadata?.safetyClassification ??
                            update.safetyClassification;

                        update.retrievalDecision =
                            data.retrievalDecision ??
                            data.workflowMetadata?.retrievalDecision ??
                            update.retrievalDecision;

                        update.conversationMomentum =
                            data.conversationMomentum ??
                            data.workflowMetadata?.conversationMomentum ??
                            update.conversationMomentum;

                        update.alignment =
                            data.alignment ??
                            data.workflowMetadata?.alignment ??
                            update.alignment;

                        update.retrievalSummary =
                            data.retrievalSummary ??
                            data.workflowMetadata?.retrievalSummary ??
                            update.retrievalSummary;
                            
                        if (data.retrievalOutput?.seeds) update.seeds = data.retrievalOutput.seeds;
                        if (Array.isArray(data.retrievedChunkIds)) {
                            update.retrievedChunkIds = data.retrievedChunkIds;
                        } else if (Array.isArray(data.retrievalOutput?.chunks)) {
                            update.retrievedChunkIds = data.retrievalOutput.chunks.map((c: any) => c.chunk.id);
                        }

                        if (Array.isArray(data.usedChunkIds)) {
                            update.usedChunkIds = data.usedChunkIds;
                        } else if (Array.isArray(data.citations)) {
                            update.usedChunkIds = data.citations
                                .map((c: any) => c.chunkId)
                                .filter(Boolean);
                        }
                        if (data.toolTrace != null) {
                            update.toolTrace = data.toolTrace;
                        }
                        if (data.goal != null) {
                            update.goal = data.goal;
                        }
                        update.isComplete = true;
                        sawCompletion = true;
                    }

                    latestUpdate = { ...latestUpdate, ...update, accumulatedContent };
                    onUpdate(latestUpdate);
                } catch (e) {
                    console.warn('Failed to parse stream chunk:', payload);
                }
            }

            if (done || stopReading) {
                break;
            }
        }

        latestUpdate = {
            ...latestUpdate,
            accumulatedContent,
            // Treat any accumulated content as a terminal state so the UI can
            // stop spinning even if the backend closes the stream without an
            // explicit `[DONE]` marker.
            isComplete: latestUpdate.isComplete || sawCompletion || accumulatedContent.length > 0,
            receivedDone: stopReading,
        };

        return latestUpdate;
    } finally {
        reader.releaseLock();
    }
}
